// ============================================================
// statusConstants.js — SINGLE SOURCE OF TRUTH for status sets
// Location: src/utils/statusConstants.js
// Python mirror: api/status_constants.py (keep the two in sync)
//
// Four production bugs came from readers disagreeing about which
// statuses mean "done" — most recently checkAllItemsDelivered
// counting only 'delivered' and leaving mixed pickup/delivery
// orders stuck open. Every "is this handled?" check MUST import
// from here.
//
// If a new status is ever added (e.g. 'refunded', 'returned'),
// decide its membership HERE, once — every reader updates itself.
//
// POST-INVENTORY NOTE (Jul 2026): the Inventory tab, invoice
// scanner, On Order view and Stock view are retired. item_statuses
// is now the PRIMARY tracking system for fulfillment. inventory_units
// is a legacy layer: no user flow creates units anymore, but delivery
// dispatch/completion and undo flows still tidy up any units that
// exist from the inventory era, so the unit groupings below stay.
// ============================================================

// ------------------------------------------------------------
// THE COMPLETE VOCABULARY — every status value the system writes.
// This is the dictionary. The groupings below are the grammar.
// ------------------------------------------------------------

/**
 * item_statuses.status — the lifecycle of one item on a receipt.
 * PRIMARY source of truth for fulfillment (post-inventory).
 *   (no row)          → item is waiting on a manufacturer shipment
 *   at_store          → physical piece in hand, staged for the customer
 *                       ("Mark ready" writes this — with or without stock)
 *   out_for_delivery  → on the truck (dispatch writes this; deleting a
 *                       dispatched route walks it back to at_store)
 *   delivered         → customer has it (delivery)
 *   picked_up         → customer has it (came and got it)
 *
 * The receipt drawer's DERIVED display vocabulary maps onto this:
 *   waiting → no row · ready/partial → at_store family ·
 *   on truck → out_for_delivery · fulfilled → delivered | picked_up
 * (getAllItemStatusesForReceipt produces the derived values; it also
 * reads legacy 'in_warehouse'/'reserved'/'ready' rows defensively.)
 *
 * Note: there is no 'returned' item status — a return is handled at
 * the unit level by releasing the unit back to 'available'.
 */
export const ITEM_STATUS_VALUES = ['at_store', 'out_for_delivery', 'delivered', 'picked_up'];

/**
 * inventory_units.status — the lifecycle of one physical unit.
 * LEGACY LAYER: nothing creates units anymore. Readers must treat
 * units as optional — every flow works with zero units — but any
 * units that do exist are still honored and advanced correctly.
 *   available         → on the floor / in the warehouse, sellable
 *   reserved          → claimed for a specific customer's receipt item
 *   out_for_delivery  → on the truck (set when a route starts)
 *   delivered         → left with a customer via delivery
 *   picked_up         → left with a customer via pickup
 * A "return" is not a status: Mark Returned sets the unit back to
 * 'available' and clears its reservation.
 */
export const UNIT_STATUS_VALUES = ['available', 'reserved', 'out_for_delivery', 'delivered', 'picked_up'];
// Legacy/defensive: some readers also accept 'at_store' and 'in_warehouse'
// on units (older data). Treat them like 'reserved' — held, not gone.

/**
 * receipts.delivery_status — the lifecycle of a whole order.
 *   pending                  → open, items still owed
 *   partially_delivered      → some items with the customer, some not
 *                              (also the "reopened" state: adding an item
 *                              to a completed receipt demotes it here)
 *   out_for_delivery         → whole order on the truck
 *   partial_out_for_delivery → some items on the truck
 *   delivered                → fully closed (last item was delivered)
 *   picked_up                → fully closed (last item was picked up)
 *   cancelled                → RESERVED: read defensively by Deliveries,
 *                              not yet written anywhere. If a cancel flow
 *                              is ever built, it's already terminal here.
 * Route STOPS (delivery_route_stops.status) are a separate domain:
 * stops go pending → delivered and are never 'picked_up' by design —
 * pickups don't ride on trucks. Stop checks may use literal 'delivered'.
 */
export const RECEIPT_DELIVERY_STATUS_VALUES = ['pending', 'partially_delivered', 'out_for_delivery', 'partial_out_for_delivery', 'delivered', 'picked_up', 'cancelled'];

// ------------------------------------------------------------
// THE GROUPINGS — which statuses mean what. Readers import these.
// ------------------------------------------------------------

/**
 * Item-level statuses (item_statuses.status) meaning the customer
 * has the item. An item in one of these states is DONE: it never
 * shows as pending and never counts as owed.
 *
 * MIXED-FULFILLMENT RULE: closure math must count BOTH members —
 * an order with one item picked up in-store and the rest delivered
 * by route is complete when every item is in this set. Consumers:
 * checkAllItemsDelivered (supabaseClient), /api/complete-delivery
 * (via status_constants.py), the receipt drawer's fulfillment
 * summary and Completed-tab filter, handleMarkUnitStatus's
 * receipt-status math.
 */
export const ITEM_TERMINAL_STATUSES = ['delivered', 'picked_up'];

/**
 * Item-level statuses meaning the physical piece has ARRIVED at the
 * store or beyond (staged, on the truck, or with the customer).
 * Used by:
 *  - handleUndoReady's "is there anything to undo?" check
 *  - Digitalreceipts' customer-facing readiness calculation
 *  - the receipt drawer's derived ready/on-truck display states
 */
export const ITEM_ARRIVED_STATUSES = ['at_store', 'out_for_delivery', 'delivered', 'picked_up'];

/**
 * Item-level statuses meaning the piece is STAGED AT THE STORE —
 * physically in hand, not yet on a truck and not yet with the
 * customer. This is ITEM_ARRIVED_STATUSES minus everything that has
 * already moved past the store.
 *
 * Added Jul 2026: the customer-facing digital receipt needs to tell
 * "ready at the store" apart from "out for delivery", and had been
 * hardcoding its own list to do it. ITEM_ARRIVED_STATUSES
 * deliberately merges the two, so readers that need the distinction
 * were freelancing — which is exactly how the receipt ended up
 * showing "Out for Delivery" on an item and "ready for delivery" in
 * the same order's summary.
 *
 * Includes legacy values ('in_warehouse', 'reserved', 'ready') that
 * older rows still carry. To a customer they all mean one thing:
 * it's here, waiting for you.
 * Consumers: Digitalreceipts' getLineStatus.
 */
export const ITEM_STAGED_STATUSES = ['at_store', 'in_warehouse', 'reserved', 'ready'];

/**
 * Unit-level statuses (inventory_units.status) meaning the physical
 * unit has left with the customer. Terminal: not sellable, not
 * reserved. Legacy layer — see UNIT_STATUS_VALUES.
 */
export const UNIT_TERMINAL_STATUSES = ['delivered', 'picked_up'];

/**
 * Receipt-level statuses (receipts.delivery_status) meaning the
 * order is fully closed out. A receipt in one of these states is
 * excluded from active orders and pending counts, and lands in the
 * Completed tab. (New Orders filters on NOT-in-this-set; Completed
 * filters on ITEM_TERMINAL_STATUSES membership — 'cancelled' would
 * appear in neither, which is intentional if it's ever written.)
 */
export const RECEIPT_TERMINAL_STATUSES = ['delivered', 'picked_up', 'cancelled'];

// ---- Convenience predicates (optional, equivalent to .includes) ----

/** True when an item's status means the customer has it. */
export const isItemTerminal = (status) => ITEM_TERMINAL_STATUSES.includes(status);

/** True when an item's status means the piece arrived (store or beyond). */
export const isItemArrived = (status) => ITEM_ARRIVED_STATUSES.includes(status);

/** True when an item's status means the piece is staged at the store. */
export const isItemStaged = (status) => ITEM_STAGED_STATUSES.includes(status);

/** True when a unit's status means it left with the customer. */
export const isUnitTerminal = (status) => UNIT_TERMINAL_STATUSES.includes(status);

/** True when a receipt's delivery_status means the order is closed. */
export const isReceiptClosed = (status) => RECEIPT_TERMINAL_STATUSES.includes(status);